import { useEffect, useRef } from 'react';
import { useFeeGuideAimCode } from '../lib/feeGuide';

// MarketApts embeds the fee guide in an iframe that auto-resizes its height via
// their hosted iframe-resizer script. The per-property AIM code selects which
// community's fees to show (the `?code=` query param).
const IFRAME_RESIZER_SRC = 'https://www.marketapts.com/js/iframeResizer.min.js';

declare global {
  interface Window {
    iFrameResize?: (options: Record<string, unknown>, target: string | HTMLElement) => void;
  }
}

interface FeeGuideEmbedProps {
  /** Optional wrapper className around the iframe. */
  className?: string;
  /** Explicit AIM property code override. When empty, uses the property's
      `custom_aim_property_code` field. Renders nothing when neither is set. */
  aimCode?: string;
}

/**
 * Shared MarketApts fee-guide iframe. Owns the AIM-code lookup + iframe-resizer
 * wiring in one place so both the inline FeeGuide section and the FeeGuideModal
 * popup render the same embed. Mounting this component loads/initializes the
 * resizer, so callers that show it conditionally (e.g. the modal) should only
 * mount it while visible. Renders nothing when the property has no AIM code.
 */
export function FeeGuideEmbed({ className, aimCode: aimCodeOverride }: FeeGuideEmbedProps) {
  // Prefer an explicit override, then per-property data. No community fallback —
  // without a code we hide the embed instead of showing another property.
  const aimCode = useFeeGuideAimCode(aimCodeOverride);

  const iframeRef = useRef<HTMLIFrameElement | null>(null);

  // Load / initialize the MarketApts iframe resizer once the iframe is mounted.
  useEffect(() => {
    if (typeof window === 'undefined' || !aimCode) return;
    const applyResize = () => {
      if (window.iFrameResize && iframeRef.current) {
        window.iFrameResize({ log: false }, iframeRef.current);
      }
    };

    // Script already loaded (e.g. another instance on the page) — just init.
    if (window.iFrameResize) {
      applyResize();
      return;
    }

    // Otherwise load the MarketApts resizer once, then initialize on load.
    let script = document.querySelector<HTMLScriptElement>(`script[src="${IFRAME_RESIZER_SRC}"]`);
    if (!script) {
      script = document.createElement('script');
      script.src = IFRAME_RESIZER_SRC;
      script.async = true;
      document.body.appendChild(script);
    }
    script.addEventListener('load', applyResize);
    return () => {
      script?.removeEventListener('load', applyResize);
    };
  }, [aimCode]);

  if (!aimCode) return null;

  const iframeSrc = `https://www.marketapts.com/iframes/fee-guide?code=${encodeURIComponent(aimCode)}`;

  return (
    <div className={className}>
      <iframe
        ref={iframeRef}
        id="fee-guide"
        title="Fee Guide"
        src={iframeSrc}
        style={{ border: 'none', width: '1px', minWidth: '100%' }}
      />
    </div>
  );
}

export default FeeGuideEmbed;
